/**
 * Alarm quick access: a shield in the app bar (and the drawer header), coloured
 * by the alarm's state, that opens the Alarm sheet: current state with when and
 * who, large arm buttons per supported mode, hold-to-disarm, Texecom partitions
 * as rows plus "All", an open-sensor warning before arming, and a link to the
 * security dashboard. The server injects the alarm code; the web never sees it.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { EntityMeta, EntityState } from '@home/shared';
import { useAccess, useEntitiesVersion, useEntity, useEntityAccess, useModel, useUser } from '../lib/hooks.js';
import { postOverride, useModeView } from '../lib/mode-api.js';
import { presenceNext, presencePressed, presenceSource, type PresenceButton } from '../lib/modes.js';
import { store } from '../lib/store.js';
import { toast } from '../lib/toast.js';
import { haptic } from '../lib/native.js';
import { Icon } from '../lib/icons.js';
import { relativeTime, formatTime } from '../lib/format.js';
import { shortName } from '../lib/entity.js';
import { alarmHome, useAlarmHome } from '../lib/ui-state.js';
import {
  ALARM_ICON,
  alarmLabel,
  alarmModes,
  alarmPanels,
  alarmTone,
  alarmZones,
  canArm,
  canDisarm,
  canSeeAlarm,
  isMotionClass,
  openSensors,
  worstTone,
  type AlarmMode,
} from '../lib/alarm.js';
import { Button, HoldButton, Segmented, Sheet } from '../components/ui/index.js';

const DISARM_HOLD_MS = 800;
/** How long the Undo stays offered after arming (typical exit delay). */
const UNDO_MS = 10_000;

/** Shield button; renders nothing for users without alarm_view or homes without a panel. */
export function AlarmShield({ home, size = 38 }: { home: string; size?: number }): ReactNode {
  const model = useModel(home);
  const access = useAccess(home);
  const panels = alarmPanels(model);
  const ids = useMemo(() => [panels.primary?.id, ...panels.partitions.map((p) => p.id)].filter((x): x is string => !!x), [panels]);
  useEntitiesVersion(home, ids);
  if (!canSeeAlarm(model, access) || !panels.primary) return null;
  const primary = store.entity(home, panels.primary.id);
  const tone = worstTone(ids.map((id) => store.entity(home, id)?.s));
  const label = tone === 'triggered' && primary?.s !== 'triggered' ? 'Triggered' : alarmLabel(primary?.s);
  return (
    <button
      type="button"
      className="icon-btn alarm-shield"
      data-tone={tone}
      aria-label={`Alarm: ${label}`}
      title={`Alarm: ${label}`}
      aria-haspopup="dialog"
      data-testid="alarm-shield"
      style={{ width: size, height: size }}
      onClick={() => alarmHome.set(home)}
    >
      <Icon name={ALARM_ICON[tone]} size={Math.round(size * 0.47)} />
    </button>
  );
}

/** One sheet for the whole app, opened by any shield. */
export function AlarmSheetHost(): ReactNode {
  const home = useAlarmHome();
  const [last, setLast] = useState<string | null>(home);
  useEffect(() => {
    if (home) setLast(home);
  }, [home]);
  const h = home ?? last;
  if (!h) return null;
  return <AlarmSheet key={h} home={h} open={home !== null} onClose={() => alarmHome.set(null)} />;
}

interface PendingArm {
  mode: AlarmMode;
  targets: string[];
  label: string;
}

function AlarmSheet({ home, open, onClose }: { home: string; open: boolean; onClose: () => void }): ReactNode {
  const model = useModel(home);
  const access = useAccess(home);
  const panels = alarmPanels(model);
  const primary = panels.primary;
  const sensors = useMemo(() => alarmZones(model), [model]);
  const sensorIds = useMemo(() => sensors.map((s) => s.id), [sensors]);
  const v = useEntitiesVersion(home, sensorIds);
  const openNow = useMemo(() => {
    void v;
    return openSensors(sensors, (id) => store.entity(home, id));
  }, [sensors, v, home]);
  const [confirm, setConfirm] = useState<PendingArm | null>(null);
  const showDash = !!model?.modules.includes('security') && !!access?.modules.includes('security');
  // The flat's "dogs home" mode: the dog cam's presence picks which PIRs can trigger the alarm.
  const dogcam = useEntity(home, 'device_tracker.dogcam');
  const dogs = !!dogcam || !!model?.entities['binary_sensor.dog_pirs'];

  useEffect(() => {
    if (!open) setConfirm(null);
  }, [open]);

  if (!primary) return null;

  const disarm = (ids: string[], label: string): void => {
    haptic('medium');
    void store
      .call(home, 'alarm_control_panel', 'alarm_disarm', ids, undefined, {
        label,
        optimistic: (s: EntityState) => ({ ...s, s: 'disarming' }),
      })
      .catch(() => undefined);
  };

  const doArm = ({ mode, targets, label }: PendingArm): void => {
    setConfirm(null);
    haptic('medium');
    void store
      .call(home, 'alarm_control_panel', mode.service, targets, undefined, {
        label,
        optimistic: (s: EntityState) => ({ ...s, s: 'arming', a: { ...s.a, next_state: mode.state } }),
      })
      .then(() => {
        toast(`Arming ${label.toLowerCase()} (${mode.label.toLowerCase()})`, {
          ttl: UNDO_MS,
          ...(canDisarm(access) ? { action: { label: 'Undo', run: () => disarm(targets, label) } } : {}),
        });
      })
      .catch(() => undefined);
  };

  const arm = (p: PendingArm): void => {
    if (openNow.length) setConfirm(p);
    else doArm(p);
  };

  return (
    <Sheet open={open} onClose={onClose} title="Alarm" subtitle={model?.home.name} icon={<Icon name="shield" size={18} />} testId="alarm-sheet">
      <div className="alarm-sheet">
        <AlarmHero home={home} id={primary.id} />
        <PresenceRow home={home} />
        {dogs && <DogsHome home={home} />}
        {confirm && (
          <div className="alarm-warn" role="alertdialog" aria-labelledby="alarm-warn-title" data-testid="alarm-open-warning">
            <div className="alarm-warn-head">
              <Icon name="alert" size={18} />
              <strong id="alarm-warn-title">
                {openNow.length === 1 ? '1 sensor is open or active' : `${openNow.length} sensors are open or active`}
              </strong>
            </div>
            <ul className="alarm-warn-list">
              {openNow.map((m) => (
                <OpenRow key={m.id} home={home} meta={m} />
              ))}
            </ul>
            <p className="alarm-warn-note">Arming now may trigger the alarm or leave these zones unprotected.</p>
            <div className="row gap-2">
              <Button onClick={() => setConfirm(null)}>Cancel</Button>
              <Button variant="primary" icon="shield-check" onClick={() => doArm(confirm)} data-testid="alarm-arm-anyway">
                Arm anyway
              </Button>
            </div>
          </div>
        )}
        <PanelControls home={home} meta={primary} onArm={arm} onDisarm={disarm} big />
        {panels.partitions.length > 1 && (
          <section className="alarm-parts" aria-label="Partitions" data-testid="alarm-partitions">
            <h3 className="eyebrow">Partitions</h3>
            <PartitionsAll home={home} parts={panels.partitions} onArm={arm} onDisarm={disarm} />
            {panels.partitions.map((p) => (
              <PanelControls key={p.id} home={home} meta={p} onArm={arm} onDisarm={disarm} />
            ))}
          </section>
        )}
        {showDash && (
          <Link className="house-sheet-all" to={`/h/${home}/dashboards/security`} onClick={onClose} data-testid="alarm-sensors-cameras">
            <Icon name="cctv" size={15} /> Sensors & cameras <Icon name="chevron" size={14} />
          </Link>
        )}
      </div>
    </Sheet>
  );
}

function AlarmHero({ home, id }: { home: string; id: string }): ReactNode {
  const st = useEntity(home, id);
  const tone = alarmTone(st?.s);
  const [by, setBy] = useState<string | null>(null);
  const lc = st?.lc ?? 0;
  // Who changed it: the logbook's context user for the latest change (HA's changed_by is the keypad/code slot).
  useEffect(() => {
    let live = true;
    setBy(null);
    if (!lc) return undefined;
    void store
      .logbook(home, id, lc - 60, lc + 60)
      .then((rows) => {
        if (!live) return;
        const row = [...rows].reverse().find((r) => r.contextUser || r.contextEvent);
        setBy(row?.contextUser ?? (row?.contextEvent ? 'an automation' : null));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [home, id, lc]);
  const changedBy = typeof st?.a['changed_by'] === 'string' && st.a['changed_by'] ? (st.a['changed_by'] as string) : null;
  const who = by ?? changedBy;
  return (
    <div className="alarm-hero" data-tone={tone} data-testid="alarm-hero">
      <span className="alarm-hero-icon" aria-hidden="true">
        <Icon name={ALARM_ICON[tone]} size={30} />
      </span>
      <div className="alarm-hero-text">
        <strong data-testid="alarm-state">{alarmLabel(st?.s)}</strong>
        {lc > 0 && (
          <span>
            Since {formatTime(lc)} ({relativeTime(lc)}){who ? ` · by ${who}` : ''}
          </span>
        )}
      </div>
    </div>
  );
}

const PRESENCE_OPTIONS = [
  { value: 'home', label: "I'm home" },
  { value: 'away', label: 'Away' },
] as const satisfies ReadonlyArray<{ value: PresenceButton; label: string }>;

/**
 * The house / flat mode's only control (members; GET /mode is 404 for guests and lodgers): "Presence: Away (auto)"
 * with "I'm home" / "Away" (the active override is shown selected; pressing it again goes back to automatic).
 */
function PresenceRow({ home }: { home: string }): ReactNode {
  const view = useModeView(home);
  const user = useUser();
  const [busy, setBusy] = useState(false);
  if (!view) return null;
  const p = view.presence;
  const run = (what: Promise<unknown>): void => {
    setBusy(true);
    what.catch((e: Error) => toast(e.message, { tone: 'alert' })).finally(() => setBusy(false));
  };
  return (
    <div className="alarm-presence" data-testid="alarm-presence" data-source={p.source}>
      <div className="alarm-presence-line">
        <span className="alarm-presence-text" data-testid="alarm-presence-label">
          Presence: <strong>{p.label}</strong> <span className="muted">({presenceSource(p, user?.id)})</span>
        </span>
        {view.canOverride && (
          <Segmented
            label="Presence"
            size="sm"
            reselect
            value={presencePressed(view)}
            options={PRESENCE_OPTIONS}
            disabled={busy}
            onChange={(b) => run(postOverride(home, { mode: presenceNext(view, b) }))}
          />
        )}
      </div>
      {view.execWarning && (
        <p className="alarm-presence-warn" data-testid="alarm-exec-warning">
          <Icon name="alert" size={14} /> {view.execWarning}
        </p>
      )}
    </div>
  );
}

function DogsHome({ home }: { home: string }): ReactNode {
  const dogcam = useEntity(home, 'device_tracker.dogcam');
  const on = dogcam?.s === 'home';
  return (
    <div className="alarm-dogs" data-on={on ? 'true' : undefined} data-testid="alarm-dogs">
      <Icon name="person" size={16} />
      <span>
        <strong>Dogs home: {on ? 'on' : 'off'}</strong>
        {on ? ' Only the dog-proof PIRs can trigger the alarm.' : ' Every PIR can trigger the alarm.'}
      </span>
    </div>
  );
}

function OpenRow({ home, meta }: { home: string; meta: EntityMeta }): ReactNode {
  const st = useEntity(home, meta.id);
  const motion = isMotionClass(meta.deviceClass);
  return (
    <li data-testid={`alarm-open-${meta.id}`}>
      <Icon name={motion ? 'motion' : 'door-open'} size={15} />
      <span>{meta.name}</span>
      <span className="muted">
        {motion ? 'Motion' : 'Open'}
        {st ? ` · ${relativeTime(st.lc)}` : ''}
      </span>
    </li>
  );
}

/** Arm buttons (one per supported mode) and hold-to-disarm for one panel; `big` for the main panel. */
function PanelControls({
  home,
  meta,
  onArm,
  onDisarm,
  big = false,
}: {
  home: string;
  meta: EntityMeta;
  onArm: (p: PendingArm) => void;
  onDisarm: (ids: string[], label: string) => void;
  big?: boolean;
}): ReactNode {
  const st = useEntity(home, meta.id);
  const access = useAccess(home);
  const ent = useEntityAccess(home, meta.id);
  const s = st?.s;
  const tone = alarmTone(s);
  const unavailable = tone === 'unknown';
  const mayArm = canArm(access) && ent.canControl && !unavailable;
  const mayDisarm = canDisarm(access) && ent.canControl && !unavailable;
  const modes = alarmModes(st);
  const label = big ? 'Alarm' : shortName(meta, 'Texecom Premier Elite 64');
  const armedOrPending = tone === 'armed' || tone === 'pending' || tone === 'triggered';
  const why = !canArm(access) && !canDisarm(access) ? 'You can see the alarm but not change it.' : !canDisarm(access) ? 'You can arm but not disarm.' : !canArm(access) ? 'You can disarm but not arm.' : null;

  if (big) {
    return (
      <div className="alarm-main">
        <div className="alarm-modes" role="group" aria-label="Arm">
          {modes.map((m) => (
            <button
              key={m.service}
              type="button"
              className="alarm-mode"
              aria-pressed={s === m.state}
              disabled={!mayArm || s === m.state || s === 'arming'}
              onClick={() => onArm({ mode: m, targets: [meta.id], label })}
              data-testid={`alarm-arm-${m.label.toLowerCase()}`}
            >
              <Icon name={m.icon} size={22} />
              <span>{s === m.state ? `Armed ${m.label.toLowerCase()}` : `Arm ${m.label.toLowerCase()}`}</span>
            </button>
          ))}
        </div>
        {armedOrPending && (
          <HoldButton
            label="Hold to disarm"
            icon="shield-off"
            tone="alert"
            ms={DISARM_HOLD_MS}
            disabled={!mayDisarm}
            onConfirm={() => onDisarm([meta.id], label)}
            testId="alarm-disarm"
          />
        )}
        {why && <p className="alarm-why muted">{why}</p>}
      </div>
    );
  }

  return (
    <div className="alarm-row" data-tone={tone} data-testid={`alarm-part-${meta.id}`}>
      <span className="alarm-row-dot" aria-hidden="true" />
      <div className="alarm-row-text">
        <strong>{label}</strong>
        <span>{alarmLabel(s)}</span>
      </div>
      <div className="alarm-row-actions">
        {armedOrPending ? (
          <HoldButton label="Disarm" holdingLabel="Hold…" icon="shield-off" tone="alert" ms={DISARM_HOLD_MS} disabled={!mayDisarm} onConfirm={() => onDisarm([meta.id], label)} />
        ) : (
          modes.slice(0, 3).map((m) => (
            <Button key={m.service} disabled={!mayArm} onClick={() => onArm({ mode: m, targets: [meta.id], label })}>
              {m.label}
            </Button>
          ))
        )}
      </div>
    </div>
  );
}

/** "All" row: arm or disarm every Texecom partition at once. */
function PartitionsAll({
  home,
  parts,
  onArm,
  onDisarm,
}: {
  home: string;
  parts: EntityMeta[];
  onArm: (p: PendingArm) => void;
  onDisarm: (ids: string[], label: string) => void;
}): ReactNode {
  const ids = useMemo(() => parts.map((p) => p.id), [parts]);
  useEntitiesVersion(home, ids);
  const access = useAccess(home);
  const states = ids.map((id) => store.entity(home, id));
  const tone = worstTone(states.map((x) => x?.s));
  const anyArmed = states.some((x) => {
    const t = alarmTone(x?.s);
    return t === 'armed' || t === 'pending' || t === 'triggered';
  });
  const armedCount = states.filter((x) => alarmTone(x?.s) === 'armed').length;
  // Modes every partition supports.
  const modes = alarmModes(states[0]).filter((m) => states.every((x) => alarmModes(x).some((o) => o.service === m.service)));
  return (
    <div className="alarm-row alarm-row-all" data-tone={tone} data-testid="alarm-part-all">
      <span className="alarm-row-dot" aria-hidden="true" />
      <div className="alarm-row-text">
        <strong>All partitions</strong>
        <span>{armedCount === 0 ? 'All disarmed' : armedCount === ids.length ? 'All armed' : `${armedCount} of ${ids.length} armed`}</span>
      </div>
      <div className="alarm-row-actions">
        {anyArmed ? (
          <HoldButton label="Disarm all" holdingLabel="Hold…" icon="shield-off" tone="alert" ms={DISARM_HOLD_MS} disabled={!canDisarm(access)} onConfirm={() => onDisarm(ids, 'All partitions')} />
        ) : (
          modes.slice(0, 3).map((m) => (
            <Button key={m.service} disabled={!canArm(access)} onClick={() => onArm({ mode: m, targets: ids, label: 'All partitions' })}>
              {m.label}
            </Button>
          ))
        )}
      </div>
    </div>
  );
}
